import { paymentsApi } from '$lib/global/payments/api';

const sha256Base64 = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = new Uint8Array(hashBuffer);

    let binary = '';
    for (let i = 0; i < hashArray.byteLength; i++) {
        binary += String.fromCharCode(hashArray[i]);
    }

    return btoa(binary);
};

// Which of the three upload steps failed (CAP-4569). Mirrors the `stage` enum of
// the payments service's POST /api/uploads/failed.
type UploadStage = 'read' | 'start' | 's3';

export class UploadError extends Error {
    constructor(
        readonly stage: UploadStage,
        cause: unknown,
        readonly status?: number,
        readonly uploadId?: string,
    ) {
        super(cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause));
        this.name = 'UploadError';
    }
}

const atStage = async <T>(stage: UploadStage, work: () => Promise<T>): Promise<T> => {
    try {
        return await work();
    } catch (err) {
        throw new UploadError(stage, err);
    }
};

class Uploader {
    private readonly companyId: string;
    private readonly campaignId: string;
    private readonly downloadUrl: string;

    constructor(companyId: string, campaignId: string) {
        this.companyId = companyId;
        this.campaignId = campaignId;
        this.downloadUrl = import.meta.env.VITE_DOWNLOAD_URL;
    }

    async uploadS3(file: File) {
        const checksum = await atStage('read', () => sha256Base64(file));

        // 1. Create pre-signed S3 upload URL
        const apiResponse = await atStage('start', () =>
            paymentsApi.s3.createSignedUrl({
                companyId: this.companyId,
                campaignId: this.campaignId,
                fileName: file.name,
                mimeType: file.type,
                size: file.size,
                checksum,
            }),
        );

        if (typeof apiResponse === 'string') {
            throw new UploadError('start', new Error(apiResponse));
        }

        const { uploadId, uploadUrl: presignedUrl } = apiResponse;

        // 2. Upload to pre-signed S3 URL
        const s3response = await fetch(presignedUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': file.type,
                'X-Amz-Sdk-Checksum-Algorithm': 'SHA256',
                'X-Amz-Checksum-Sha256': checksum,
            },
            body: file,
        }).catch((err) => {
            throw new UploadError('s3', err, undefined, uploadId);
        });

        if (!s3response.ok) {
            throw new UploadError(
                's3',
                new Error(`Failed to upload file to S3: ${s3response.statusText}`),
                s3response.status,
                uploadId,
            );
        }

        const queryParams = `filename=${file.name}&filesize=${file.size}`;

        return `${this.downloadUrl}/download/${uploadId}?${encodeURI(queryParams)}`;
    }

    // The lead is submitted with `failed-upload` either way; this only makes the
    // failure visible to us. The file name is left out on purpose — applicants
    // routinely name their files after themselves.
    reportFailure(err: unknown, file: File) {
        const uploadError = err instanceof UploadError ? err : undefined;

        paymentsApi.s3.reportUploadFailure({
            companyId: this.companyId,
            campaignId: this.campaignId,
            stage: uploadError?.stage ?? 'unknown',
            status: uploadError?.status,
            message: uploadError?.message ?? String(err),
            mimeType: file.type,
            size: file.size,
            uploadId: uploadError?.uploadId,
        });
    }
}

export default Uploader;
